import ListActions from '@components/List/ListActions'
import ListLayout from '@components/List/ListLayout'
import { REQUEST_DEFAULT_LIMIT, REQUEST_DEFAULT_OFFSET } from '@utils/constants/pagination'
import { isEmptyString, isNullOrUndefined, isObject, isString } from '@utils/types'
import { Button } from 'primereact/button'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * Read-only looking input displaying the currently selected value, used by `SelectArray` to
 * trigger the item picker on click and optionally clear the current value.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.value] - Value of the input, or null/empty if nothing is selected.
 * @param {boolean} [props.invalid=false] - Show the input as invalid.
 * @param {Function} props.onClick - Called when the input is clicked (opens the picker).
 * @param {Function} [props.onCancel] - Called when the cancel button is clicked (clears the value).
 * @returns {JSX.Element}
 */
function FakeInput({
  value, // value of the input (string) or null
  invalid = false, // boolean to show the input as invalid
  onClick, // function to call when the input is clicked
  onCancel // function to call when the cancel button is clicked
}) {
  const { t } = useTranslation()

  /**
   * Builds the CSS class string for the fake input, adding the invalid state class when needed.
   *
   * @returns {string}
   */
  const getInputClassNames = () => {
    const classNames = [
      'w-full',
      'p-inputtext',
      'p-component',
      'p-filled',
      ' cursor-pointer',
      'pr-0'
    ]

    if (invalid) classNames.push('p-invalid')

    return classNames.join(' ')
  }

  FakeInput.propTypes = {
    value: PropTypes.string,
    invalid: PropTypes.bool,
    onClick: PropTypes.func,
    onCancel: PropTypes.func
  }

  /**
   * Handles the cancel button click: stops it from also triggering the fake input's own click
   * handler, and calls `onCancel` if provided.
   *
   * @param {Event} e - Click event from the cancel button.
   * @returns {void}
   */
  const onCancelClick = e => {
    e.preventDefault()
    e.stopPropagation() // avoid to trigger the onClick event
    if (onCancel) onCancel()
  }

  return (
    <div
      className={getInputClassNames()}
      onClick={onClick}
    >
      <div className="fake-input relative flex align-items-center justify-content-between">
        <span>{value ?? ''}</span>
        {onCancel && value && (
          <span>
            <Button
              icon="fa-solid fa-xmark"
              onClick={onCancelClick}
              type="button"
              rounded
              text
              className="fake-input-cancel"
              aria-label={t('common.cancel')}
            />
          </span>
        )}
      </div>
    </div>
  )
}

/**
 * Field that lets the user pick a single item from a remote, paginated and filterable list.
 * Shows the current selection in a `FakeInput` and opens a `ListLayout` overlay (fed by `getItems`)
 * to change or clear it; calls `onChange({ value })` whenever the selection changes.
 *
 * @component
 * @param {Object} props
 * @param {Array} props.columns - Column definitions passed to the item list (`ListLayout`).
 * @param {Function} props.onChange - Called with `{ value }` when the selection changes or is cleared.
 * @param {boolean} [props.disabled=false] - Disable the selection button.
 * @param {boolean} [props.invalid=false] - Show the input in an invalid/error state.
 * @param {Function} props.getItems - Retrieves the items to display; called with query params (filters, pagination, sort).
 * @param {string} [props.keyLabel] - Key used to read the display label off the selected item.
 * @param {Function} [props.getLabel] - Computes the display label from the selected item (takes precedence over `keyLabel`).
 * @param {string} [props.selectKey] - Key used to extract the value passed to `onChange` from the selected item (defaults to the whole item).
 * @param {Object|string} props.value - Current selected value (must be an object, unless empty).
 * @param {boolean} [props.required=false] - Render a hidden required input to enforce native form validation.
 * @param {number} [props.defaultLimit] - Initial page size.
 * @param {number} [props.defaultOffset] - Initial page offset.
 * @returns {JSX.Element}
 */
export default function SelectArray({
  columns, // columns to display for the table
  onChange, // hook called when the value is changed
  disabled = false, // boolean to disable the input
  invalid = false, // boolean to show the input as invalid

  getItems, // function to get the items to display

  /** Label transformation */
  keyLabel,
  getLabel,

  /** Value transformation */
  selectKey,

  value, // initial value must be an object !
  required = false,
  defaultLimit = REQUEST_DEFAULT_LIMIT,
  defaultOffset = REQUEST_DEFAULT_OFFSET
}) {
  SelectArray.propTypes = {
    columns: PropTypes.array.isRequired,
    columnsFilter: PropTypes.array,
    onChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    invalid: PropTypes.bool,
    getItems: PropTypes.func.isRequired,
    keyLabel: PropTypes.string,
    getLabel: PropTypes.func,
    selectKey: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    required: PropTypes.bool,
    defaultLimit: PropTypes.number,
    defaultOffset: PropTypes.number
  }

  const [visible, setVisible] = useState(false)
  const { t } = useTranslation()
  const [currentItem, setCurrentItem] = useState(null)
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [filters, setFilters] = useState({})
  const [showFilters, setShowFilters] = useState(false)
  const [limit, setLimit] = useState(defaultLimit)
  const [offset, setOffset] = useState(defaultOffset)
  const [sortOrder, setSortOrder] = useState(null)
  const [sortField, setSortField] = useState(null)
  const [count, setCount] = useState(0)
  /**
   * Reset the offset when the filters are changed and retrieve the items
   **/
  useEffect(() => {
    if (visible) {
      offset ? setOffset(defaultOffset) : retrieveItems(queryParams())
    }
  }, [filters, sortField, sortOrder])

  /**
   * Get the items to display when the component is "opened"
   * - get the values to filter on
   * - get the items to display
   */
  useEffect(() => {
    if (visible) {
      retrieveItems(queryParams())
    }
  }, [visible, limit, offset])

  /**
   * Reload the current item when the value is changed
   */
  useEffect(() => {
    setCurrentItem(value)
    setVisible(false)
  }, [value])

  /**
   * Help to debug the value of the SelectArray, this avoid a blank page on the UI
   */
  if (!isObject(value) && !isNullOrUndefined(value) && isEmptyString(value)) {
    return (
      <div className="bg-red-500 text-center font-bold text-0">
        {t('common.select_array_invalid_value', { type: typeof value })}
      </div>
    )
  }

  /**
   * Fetches items via `getItems` and updates the items/count state, filling in a fallback `id`
   * (the array index) for results that don't carry one.
   *
   * @param {Object} [queryParams] - Query params (filters, pagination, sort) sent to `getItems`.
   * @returns {Promise<void>}
   */
  const retrieveItems = async (queryParams = { limit, offset }) => {
    setLoading(true)
    await getItems(queryParams)
      .then(({ data }) => {
        data.results &&
          setItems(
            data.results.map((item, index) => ({
              ...item,
              // Unique id is mandatory to render properly the list with
              // In case data do not contain an id key, we fill it with the entry index
              id: item.id ?? index
            }))
          )
        data.count && setCount(data.count)
      })
      .finally(() => setLoading(false))
  }

  /**
   * Builds the query params for the next `retrieveItems` call from the current filters,
   * pagination and sort state.
   *
   * @returns {{
   *   limit: number, // Current page size.
   *   offset: number, // Current page offset.
   *   ordering: (string|undefined) // Sort field prefixed with "-" for descending order, undefined when unsorted.
   * }} Current filters merged with the pagination and sort params.
   */
  const queryParams = () => {
    return {
      ...filters,
      limit,
      offset,
      ordering: sortField && `${sortOrder > 0 ? '' : '-'}${sortField}`
    }
  }

  /**
   * Selects the item with the given id from the current `items` list: stores it (or the value at
   * `selectKey` within it, when provided), notifies `onChange`, and closes the picker.
   *
   * @param {*} id - Id of the row that was clicked in the item list.
   * @returns {void}
   */
  const select = id => {
    let selectedItem = items.find(item => item.id === id)

    // If selectKey is not provided, use it to get the selected item
    if (selectKey) {
      if (isNullOrUndefined(selectedItem[selectKey])) {
        throw new Error(`SelectArray: "${selectKey}" is not a valid key for the selected item`)
      }
      selectedItem = selectedItem[selectKey]
    }
    setCurrentItem(selectedItem)
    onChange({ value: selectedItem })
    setVisible(false)
  }

  /**
   * Computes the display label for the currently selected item, using `getLabel` or `keyLabel`
   * when provided, falling back to the item itself when it's already a string.
   *
   * @returns {string}
   */
  const getSelectedValueLabel = () => {
    if (isNullOrUndefined(currentItem)) return ''
    if (getLabel) return getLabel(currentItem)
    if (keyLabel) {
      if (!isNullOrUndefined(currentItem[keyLabel])) return currentItem[keyLabel]
      else
        throw new Error(
          `SelectArray: ${keyLabel} is not a valid key for the selected item ${currentItem}`
        )
    } else if (isString(currentItem)) {
      return currentItem
    }

    return ''
  }

  /**
   * Toggles the visibility of the item picker overlay.
   *
   * @returns {void}
   */
  const toggleVisibility = () => {
    setVisible(!visible)
  }

  /**
   * Clears the current selection and notifies `onChange` with a null value.
   *
   * @returns {void}
   */
  const cancelCurrentChoice = () => {
    setCurrentItem(null)
    onChange({ value: null })
  }

  return (
    <>
      <div className="flex">
        {required && (
          <input
            type="text"
            value={getSelectedValueLabel()}
            // prevent the input to be modified directly
            // we can't use readOnly because it will be submitted without checking the
            // required attribute
            onChange={() => {}}
            className="hidden"
            required={required}
          />
        )}
        <FakeInput
          value={getSelectedValueLabel()}
          invalid={invalid}
          onClick={toggleVisibility}
          onCancel={cancelCurrentChoice}
        />

        <Button
          rounded
          className="border-noround-right border-noround-left"
          label={t('common.select_placeholder')}
          disabled={disabled}
          type="button"
          onClick={toggleVisibility}
        />
      </div>

      {visible && (
        <div className="select-array">
          <Button
            className="close-button"
            rounded
            icon="fa-solid fa-times"
            onClick={() => setVisible(false)}
            type="button"
            text
          />
          <ListActions
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            initialFiltersVisible={Object.keys(filters).length > 0}
          />
          <ListLayout
            className="select-array-list"
            columns={columns}
            value={items}
            filters={filters}
            setFilters={setFilters}
            showFilters={showFilters}
            onRowClick={select}
            scrollable={true}
            scrollHeight="400px"
            loading={loading}
            offset={offset}
            limit={limit}
            setOffset={setOffset}
            setLimit={setLimit}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
            sortField={sortField}
            setSortField={setSortField}
            backendSort={true}
            count={count}
          />
        </div>
      )}
    </>
  )
}

SelectArray.displayName = 'SelectArray'
